import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Cpu } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Create Account | Paw Supplies Group';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    login({ 
      email: formData.email, 
      firstName: formData.firstName, 
      lastName: formData.lastName 
    });
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-[140px] pb-20">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-titan-600 to-titan-500 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Cpu className="w-8 h-8 text-brand-brown" />
            </div>
            <h1 className="font-display text-3xl text-brand-brown uppercase tracking-tight mb-2">Create Account</h1>
            <p className="text-neutral-400">Join the Titan community</p>
          </div>
          
          <div className="bg-brand-cream border border-neutral-800 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-xl">
                  {error}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-brand-brown">First Name *</Label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="mt-2 bg-neutral-800 border-neutral-700 text-brand-brown placeholder-neutral-500 focus:ring-titan-500 focus:border-titan-500 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-brand-brown">Last Name</Label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="mt-2 bg-neutral-800 border-neutral-700 text-brand-brown placeholder-neutral-500 focus:ring-titan-500 focus:border-titan-500 rounded-xl"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-brand-brown">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 bg-neutral-800 border-neutral-700 text-brand-brown placeholder-neutral-500 focus:ring-titan-500 focus:border-titan-500 rounded-xl"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-brand-brown">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-2 bg-neutral-800 border-neutral-700 text-brand-brown placeholder-neutral-500 focus:ring-titan-500 focus:border-titan-500 rounded-xl"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-brand-brown">Confirm Password *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="mt-2 bg-neutral-800 border-neutral-700 text-brand-brown placeholder-neutral-500 focus:ring-titan-500 focus:border-titan-500 rounded-xl"
                  placeholder="••••••••"
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full py-4 bg-gradient-to-r from-titan-600 to-titan-500 hover:from-titan-500 hover:to-titan-400 text-brand-brown font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
              >
                Create Account
              </button>
            </form>
          </div>
          
          <p className="text-center mt-6 text-neutral-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-titan-500 font-medium hover:text-titan-400">
              Sign in
            </Link>
          </p>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RegisterPage;
